import React from 'react';
import { Phone, Mail, Globe, QrCode, Share2, Radio, UserPlus } from 'lucide-react';
import { ContactInfo } from '../types';

interface ActionButtonsProps {
  contactInfo: ContactInfo;
  onShowQR: () => void;
  onShowShare: () => void;
  onShowBroadcast: () => void;
  onShowEmail: () => void;
  isBroadcastEnabled?: boolean; // kept for compatibility; no longer disables the button
  onAnalyticsUpdate?: (metric: string) => void;
  onContactExchange?: () => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  contactInfo,
  onShowQR,
  onShowShare,
  onShowBroadcast,
  onShowEmail,
  isBroadcastEnabled = true,
  onAnalyticsUpdate,
  onContactExchange
}) => {
  const handleCall = () => {
    window.open(`tel:${contactInfo.phone}`, '_self');
    // Dedicated callClicks metric (was contactClicks); improves accuracy of Call Clicks tile
    onAnalyticsUpdate?.('callClicks');
  };

  const handleEmail = () => {
    onShowEmail();
    onAnalyticsUpdate?.('messageClicks');
  };

  const handleWebsite = () => {
    window.open(contactInfo.website, '_blank');
    onAnalyticsUpdate?.('contactClicks');
  };

  const topButtons = [
    {
      icon: Phone,
      label: 'Call',
      onClick: handleCall
    },
    {
      icon: Mail,
      label: 'Email',
      onClick: handleEmail
    },
    {
      icon: Globe,
      label: 'Website',
      onClick: handleWebsite
    }
  ];

  const bottomButtons = [
    {
      icon: QrCode,
      label: 'QR Code',
      onClick: onShowQR
    },
    {
      icon: Share2,
      label: 'Share',
      onClick: onShowShare
    },
    {
      icon: Radio,
      label: 'Broadcast',
      onClick: onShowBroadcast
    }
  ];

  return (
    <div className="px-6 mb-8 mt-6">
      {/* Top row buttons */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        {topButtons.map((button, index) => {
          const IconComponent = button.icon;
          return (
            <button
              key={index}
              onClick={button.onClick}
              className="flex items-center justify-center gap-1.5 py-3 px-2 bg-white border border-gray-200 rounded-lg font-medium transition-all duration-200 hover:shadow-md hover:border-gray-300 hover:bg-gray-50 min-h-[50px]"
            >
              <IconComponent className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <span className="text-xs text-gray-700 font-medium leading-tight">{button.label}</span>
            </button>
          );
        })}
      </div>

      {/* Bottom row buttons */}
    <div className="grid grid-cols-3 gap-4">
        {bottomButtons.map((button, index) => {
          const IconComponent = button.icon;
      const isBroadcast = button.label.toLowerCase() === 'broadcast';
      const disabledLook = isBroadcast && !isBroadcastEnabled;
          return (
            <button
              key={index}
              onClick={button.onClick}
        aria-disabled={disabledLook ? true : undefined}
        className={`flex items-center justify-center gap-1.5 py-3 px-2 bg-white border border-gray-200 rounded-lg font-medium transition-all duration-200 min-h-[50px] cursor-pointer ${disabledLook ? 'opacity-60 hover:shadow-none hover:border-gray-200 hover:bg-white cursor-not-allowed' : 'hover:shadow-md hover:border-gray-300 hover:bg-gray-50'}`}
            >
              <IconComponent className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 leading-tight">{button.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ActionButtons;